import { useState, useEffect, useCallback } from "react";
import DashboardTab from "./components/DashboardTab.js";
import ConnectionTab from "./components/ConnectionTab.js";
import LogsTab from "./components/LogsTab.js";
import BillingTab from "./components/BillingTab.js";
import UsersTab from "./components/UsersTab.js";
import HubSpotUsersTab from "./components/HubSpotUsersTab.js";
import AccountSwitcher from "./components/AccountSwitcher.js";
import SettingsTab from "./components/SettingsTab.js";
import { NotificationBell } from "./components/NotificationBell.js";
import LandingPage from "./pages/LandingPage.js";
import FaqPage from "./pages/FaqPage.js";
import TermsPage from "./pages/TermsPage.js";
import PrivacyPage from "./pages/PrivacyPage.js";
import ContactPage from "./pages/ContactPage.js";
import GuidePage from "./pages/guide/GuidePage.js";
import { apiFetch, api } from "./api.js";
import {
  parseRoute,
  pathForTab,
  isDashboardPath,
  resolveTabForRole,
  type Tab,
} from "./dashboardRoute.js";

const TABS: { key: Tab; label: string }[] = [
  { key: "dashboard", label: "ダッシュボード" },
  { key: "logs", label: "送信ログ" },
  { key: "connection", label: "接続設定" },
  { key: "users", label: "ユーザー" },
  { key: "hubspot-users", label: "HubSpot ユーザー権限" },
  { key: "settings", label: "設定" },
  { key: "billing", label: "プラン・請求" },
];

// ─── 共通ロゴ（Sign Extension for クラウドサイン） ───
const LOGO_URL = "https://21050458.fs1.hubspotusercontent-na2.net/hubfs/21050458/Extension%20Spot/Sign%20Extension%20for%20%E3%82%AF%E3%83%A9%E3%82%A6%E3%83%89%E3%82%B5%E3%82%A4%E3%83%B3.png";

function Logo({ size = 48, block = true }: { size?: number; block?: boolean }) {
  return (
    <img
      src={LOGO_URL}
      alt="Sign Extension for クラウドサイン"
      style={{
        height: size,
        width: "auto",
        display: block ? "block" : "inline-block",
        margin: block ? "0 auto 20px" : 0,
        objectFit: "contain",
      }}
    />
  );
}

// ─── 登録ページ ───
function RegisterPage() {
  const [submitted, setSubmitted] = useState<string | null>(null); // 送信完了時のメールアドレス
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [inviteMatched, setInviteMatched] = useState<{ tenantName: string; role: string } | null>(null);

  useEffect(() => {
    // HubSpot フォーム埋め込み
    const script = document.createElement("script");
    script.src = "//js-na2.hsforms.net/forms/embed/v2.js";
    script.charset = "utf-8";
    script.onload = () => {
      (window as any).hbspt?.forms?.create({
        portalId: "21050458",
        formId: "b4323c3b-0658-47c1-b5cc-c7357aebcf6c",
        region: "na2",
        target: "#hs-form-container",
        onFormSubmitted: ($form: any, data: any) => {
          // email 取得（submissionValues.email が標準）
          const email =
            data?.submissionValues?.email ||
            data?.submission?.values?.find((v: any) => v.name === "email")?.value ||
            $form?.find?.("input[name='email']")?.val?.();

          if (!email) {
            setSubmitError("メールアドレスを取得できませんでした。時間をおいて再度お試しください。");
            return;
          }

          // User + Tenant を自動作成 + マジックリンク送信
          fetch("/api/auth/magic-link", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, allowSignup: true }),
          })
            .then(async (r) => {
              if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.error || "登録処理に失敗しました");
              }
              const body = await r.json().catch(() => ({}));
              if (body.isInvitation && body.tenantName) {
                setInviteMatched({ tenantName: body.tenantName, role: body.role || "member" });
              }
              setSubmitted(email);
            })
            .catch((e: any) => setSubmitError(e.message));
        },
      });
    };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // 送信完了状態
  if (submitted) {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 12 }}>メールを確認してください</h1>
        {inviteMatched ? (
          <>
            <div style={{ padding: "14px 16px", borderRadius: 8, background: "#EEEDFE", color: "#534AB7", fontSize: 14, marginBottom: 20, textAlign: "left", lineHeight: 1.6 }}>
              <strong>{inviteMatched.tenantName}</strong> への招待が見つかりました。<br />
              新規登録ではなく、既存チームへ <strong>{inviteMatched.role === "admin" ? "管理者" : "メンバー"}</strong> として参加します。
            </div>
            <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
              <strong>{submitted}</strong> に招待確認用のログインリンクを送信しました。<br />
              メール内のボタンをクリックしてチームに参加してください。
            </p>
          </>
        ) : (
          <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
            <strong>{submitted}</strong> にログインリンクを送信しました。<br />
            メール内のボタンをクリックしてアカウントを有効化してください。
          </p>
        )}
        <div style={{ padding: "14px 20px", background: "#faf9f7", borderRadius: 10, fontSize: 13, color: "#6b6b68" }}>
          リンクの有効期限は30分です。届かない場合は迷惑メールフォルダをご確認ください。
        </div>
        <a href="/login" style={{ display: "inline-block", marginTop: 20, color: "#534AB7", fontSize: 14, textDecoration: "none" }}>
          ログインページへ
        </a>
      </div>
    );
  }

  return (
    <div className="register-layout" style={{ minHeight: "100vh", display: "flex", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* 左カラム: サービス紹介 */}
      <div className="register-left" style={{ flex: 1, background: "#534AB7", color: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 40px" }}>
        <div style={{ maxWidth: 440 }}>
          <div style={{ marginBottom: 28, background: "white", display: "inline-block", padding: "10px 16px", borderRadius: 12 }}>
            <img
              src={LOGO_URL}
              alt="Sign Extension for クラウドサイン"
              style={{ height: 44, width: "auto", display: "block" }}
            />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16, lineHeight: 1.4 }}>
            Sign Extension<br />for クラウドサイン
          </h1>
          <p style={{ fontSize: 17, opacity: 0.9, lineHeight: 1.8, marginBottom: 32 }}>
            HubSpot CRM から直接クラウドサインの電子契約を送信・管理。契約業務を効率化します。
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { icon: "1", text: "HubSpot CRM レコードから直接送信" },
              { icon: "2", text: "ステータスを自動同期" },
              { icon: "3", text: "締結完了ファイルを HubSpot に格納" },
            ].map((item) => (
              <div key={item.icon} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: 15 }}>{item.text}</span>
              </div>
            ))}
          </div>

          {/* HubSpot 導入・運用支援の案内 */}
          <div style={{ marginTop: 36, padding: "20px 22px", borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(6px)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>HubSpot 導入・運用支援</div>
            <div style={{ fontSize: 13, lineHeight: 1.8, opacity: 0.95, marginBottom: 12 }}>
              HubSpot の導入や運用を国内トップクラスの認定パートナーがサポートします。<br />
              お気軽にご相談ください。
            </div>
            <a href="/contact" style={{ display: "inline-block", padding: "8px 16px", border: "1px solid white", borderRadius: 8, color: "white", textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
              お問い合わせはこちら
            </a>
          </div>
        </div>
      </div>

      {/* 右カラム: 登録フォーム */}
      <div className="register-right" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 40px", background: "#faf9f7" }}>
        <div style={{ maxWidth: 520, width: "100%" }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>アカウント登録</h2>
          <p style={{ color: "#6b6b68", marginBottom: 28, fontSize: 15 }}>
            以下のフォームを入力して登録してください
          </p>
          {submitError && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>
              {submitError}
            </div>
          )}
          <div style={{ background: "white", border: "1px solid #e8e7e3", borderRadius: 12, padding: "32px 32px", overflow: "visible" }}>
            <div id="hs-form-container" style={{ overflow: "visible" }} />
          </div>
          <p style={{ marginTop: 20, fontSize: 14, color: "#6b6b68" }}>
            既にアカウントをお持ちですか？ <a href="/login" style={{ color: "#534AB7", textDecoration: "none", fontWeight: 500 }}>ログイン</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── ログインページ ───
function LoginPage() {
  const params = new URLSearchParams(window.location.search);
  const inviteTokenParam = params.get("invite");
  const invitedEmailParam = params.get("email");
  const errorParam = params.get("error");

  const [email, setEmail] = useState(invitedEmailParam || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notRegistered, setNotRegistered] = useState(errorParam === "not_registered");
  const [inviteError, setInviteError] = useState<string | null>(
    errorParam === "invite_invalid" ? "招待リンクが無効または期限切れです" :
    errorParam === "invite_email_mismatch" ? "招待されたメールアドレスと一致しません" :
    null,
  );
  // HubSpot 認可の中断理由。他テナントの存在を推測できる情報（メールアドレス・
  // 会社名・ポータルID）は含めない。
  const oauthError =
    errorParam === "oauth_login_required" ? "HubSpot の接続は完了しました。続けるにはログインしてください。" :
    errorParam === "oauth_portal_conflict" ? "この HubSpot ポータルは、現在ログイン中のアカウントでは接続できません。設定は変更していません。お手数ですがサポートまでお問い合わせください。" :
    errorParam === "oauth_session_mismatch" ? "接続の途中でログイン状態が変わったため中断しました。設定は変更していません。もう一度お試しください。" :
    errorParam === "oauth_start_from_dashboard" ? "HubSpot の接続は、ログインした管理画面から開始してください。設定は変更していません。" :
    null;
  const [inviteInfo, setInviteInfo] = useState<{ tenantName: string; role: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [alreadyLoggedInAs, setAlreadyLoggedInAs] = useState<string | null>(null);

  // 招待情報の事前検証
  useEffect(() => {
    if (!inviteTokenParam) return;
    api.validateInvite(inviteTokenParam)
      .then((r: any) => setInviteInfo({ tenantName: r.tenantName, role: r.role }))
      .catch((e: any) => setInviteError(e.message || "招待リンクが無効です"));
  }, []);

  // 招待リンクを開いた際、既に別のメールでログイン中なら警告
  useEffect(() => {
    if (!inviteTokenParam || !invitedEmailParam) return;
    // ログイン中判定は Cookie で自動送信。401 なら未ログイン扱い。
    fetch("/api/tenants/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t: any) => {
        const myEmail = t?.user?.email;
        if (myEmail && myEmail.toLowerCase() !== invitedEmailParam.toLowerCase()) {
          setAlreadyLoggedInAs(myEmail);
        }
      })
      .catch(() => {});
  }, []);

  // 再送クールダウンのタイマー
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendMagicLink = async () => {
    setSending(true);
    setError(null);
    setNotRegistered(false);
    try {
      await apiFetch("/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({
          email,
          inviteToken: inviteTokenParam || undefined,
        }),
      });
      setSent(true);
      setCooldown(60);
    } catch (err: any) {
      if (err.message?.includes("未登録") || err.message?.includes("NOT_REGISTERED")) {
        setNotRegistered(true);
      } else {
        setError(err.message);
      }
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMagicLink();
  };

  const googleHref = inviteTokenParam
    ? `/api/auth/google/authorize?invite=${encodeURIComponent(inviteTokenParam)}`
    : "/api/auth/google/authorize";

  if (sent) {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 12 }}>メールを確認してください</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
          <strong>{email}</strong> にログインリンクを送信しました。<br />
          メール内のボタンをクリックしてログインしてください。
        </p>
        <div style={{ padding: "14px 20px", background: "#faf9f7", borderRadius: 10, fontSize: 13, color: "#6b6b68", marginBottom: 20 }}>
          リンクの有効期限は30分です。届かない場合は迷惑メールフォルダをご確認ください。
        </div>
        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>
            {error}
          </div>
        )}
        <button
          onClick={sendMagicLink}
          disabled={cooldown > 0 || sending}
          style={{
            padding: "10px 28px", background: cooldown > 0 ? "#e8e7e3" : "#f5f4f0", color: cooldown > 0 ? "#999" : "#1a1a1a",
            border: "1px solid #e8e7e3", borderRadius: 8, fontSize: 14, fontWeight: 500,
            cursor: cooldown > 0 || sending ? "not-allowed" : "pointer", marginRight: 10,
          }}
        >
          {sending ? "送信中..." : cooldown > 0 ? `メールを再送信（${cooldown}秒）` : "メールを再送信"}
        </button>
        <button onClick={() => { setSent(false); setEmail(""); setError(null); }}
          style={{ background: "none", border: "none", color: "#534AB7", fontSize: 14, cursor: "pointer" }}>
          別のメールアドレスで試す
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: "120px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
      <Logo />
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 12 }}>ログイン</h1>
      <p style={{ color: "#6b6b68", marginBottom: 32, fontSize: 15 }}>
        メールアドレスまたは Google アカウントでログイン
      </p>

      {/* 既にログイン中の警告 */}
      {alreadyLoggedInAs && (
        <div style={{ padding: "14px 16px", borderRadius: 8, background: "#FAEEDA", color: "#854F0B", fontSize: 14, marginBottom: 16, textAlign: "left", lineHeight: 1.6 }}>
          現在 <strong>{alreadyLoggedInAs}</strong> でログイン中です。<br />
          別アカウント <strong>{invitedEmailParam}</strong> で招待を受諾するには、先にログアウトしてください。<br />
          <button
            onClick={async () => {
              // サーバー側で Cookie をクリアしてからローカル状態を更新
              await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
              setAlreadyLoggedInAs(null);
            }}
            style={{ marginTop: 6, padding: "6px 14px", background: "#854F0B", color: "white", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer" }}
          >
            ログアウトして続行
          </button>
        </div>
      )}

      {/* 招待情報バナー */}
      {inviteInfo && !alreadyLoggedInAs && (
        <div style={{ padding: "14px 16px", borderRadius: 8, background: "#EEEDFE", color: "#534AB7", fontSize: 14, marginBottom: 16, textAlign: "left", lineHeight: 1.6 }}>
          <strong>{inviteInfo.tenantName}</strong> に{inviteInfo.role === "admin" ? "管理者" : "メンバー"}として招待されています。<br />
          下記のメールアドレスでログインしてください。
        </div>
      )}

      {/* HubSpot 認可の中断 */}
      {oauthError && (
        <div style={{ padding: "14px 16px", borderRadius: 8, background: "#FAEEDA", color: "#854F0B", fontSize: 14, marginBottom: 16, textAlign: "left", lineHeight: 1.6 }}>
          {oauthError}
        </div>
      )}

      {/* 招待エラー */}
      {inviteError && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16, textAlign: "left", lineHeight: 1.6 }}>
          {inviteError}
        </div>
      )}

      {/* 未登録エラー */}
      {notRegistered && (
        <div style={{ padding: "14px 16px", borderRadius: 8, background: "#FAEEDA", color: "#854F0B", fontSize: 14, marginBottom: 16, textAlign: "left", lineHeight: 1.6 }}>
          未登録です。新規登録または招待が必要です。<br />
          <a href="/register" style={{ color: "#534AB7", fontWeight: 500 }}>新規登録はこちら →</a>
        </div>
      )}

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Google OAuth ボタン */}
      <a
        href={googleHref}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          width: "100%", padding: "14px 24px", background: "white", color: "#1a1a1a",
          border: "1px solid #dadce0", borderRadius: 8, fontSize: 15, fontWeight: 500,
          textDecoration: "none", boxSizing: "border-box", marginBottom: 20,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Google でログイン
      </a>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, color: "#999", fontSize: 13 }}>
        <div style={{ flex: 1, height: 1, background: "#e8e7e3" }} />
        <span>または</span>
        <div style={{ flex: 1, height: 1, background: "#e8e7e3" }} />
      </div>

      <form onSubmit={handleSubmit}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="メールアドレス"
          required
          readOnly={!!invitedEmailParam}
          style={{
            width: "100%", padding: "14px 16px", fontSize: 16, border: "1px solid #e8e7e3",
            borderRadius: 8, boxSizing: "border-box", marginBottom: 16, outline: "none",
            background: invitedEmailParam ? "#faf9f7" : "white",
            color: invitedEmailParam ? "#6b6b68" : "#1a1a1a",
          }}
        />
        <button
          type="submit"
          disabled={sending || !email}
          style={{ width: "100%", padding: "14px 24px", background: "#534AB7", color: "white", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer", opacity: sending || !email ? 0.6 : 1 }}
        >
          {sending ? "送信中..." : "ログインリンクを送信"}
        </button>
      </form>

      <p style={{ marginTop: 20, fontSize: 14, color: "#6b6b68" }}>
        アカウントをお持ちでないですか？ <a href="/register" style={{ color: "#534AB7", textDecoration: "none", fontWeight: 500 }}>登録する</a>
      </p>
    </div>
  );
}

// ─── メール確認ページ ───
// テストから直接レンダリングするため export する（__tests__/verifyPage.test.tsx）
export function VerifyPage() {
  const [status, setStatus] = useState<
    "loading" | "confirm" | "verifying" | "success" | "error" | "blocked" | "connecting"
  >("loading");

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  /**
   * ログイン後の振り分け。HubSpot 接続済みならダッシュボード、
   * 未接続なら OAuth を開始する。トークン経由でもそれ以外でも同じ判定を通す。
   */
  const routeAfterLogin = () => {
    fetch("/api/tenants/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((tenant: any) => {
        if (!tenant) {
          setStatus("error");
          return;
        }
        const isHubSpotConnected = tenant?.portalId && !String(tenant.portalId).startsWith("pending_");
        if (isHubSpotConnected) {
          setStatus("success");
          setTimeout(() => { window.location.href = "/dashboard"; }, 800);
        } else {
          setStatus("connecting");
          setTimeout(() => { window.location.href = "/api/auth/authorize"; }, 1500);
        }
      })
      .catch(() => setStatus("error"));
  };

  useEffect(() => {
    if (params.get("error")) {
      setStatus("error");
      return;
    }

    /**
     * メールの直リンク（?token=…）で来た場合は **ここで消費しない**。
     *
     * 以前はこの時点で /api/auth/verify-magic へ自動遷移していたため、
     * メールスキャナが URL を開いただけでリンクが使い切られていた
     * （ops/MAGIC_LINK_SAFELINKS.md）。ボタンを押すまで何もしない。
     * GET を呼ばないので、サーバー側の 302 とのループも起きない。
     */
    if (token) {
      setStatus("confirm");
      return;
    }

    // トークンが無い場合は既にサーバーが Cookie をセットしている
    routeAfterLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ボタン押下で初めて消費する */
  const handleVerify = async () => {
    if (!token) return;
    setStatus("verifying");
    try {
      const res = await fetch("/api/auth/verify-magic", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        /**
         * 403 は Origin ヘッダが付かなかった／別ドメインだった場合。
         * リンク自体は有効なので「無効・期限切れ」と同じ文言を出すと誤解を招く。
         * 実際に起こり得るのはブラウザ拡張やプライバシー保護機能が
         * Origin を落としているケース。
         */
        setStatus(res.status === 403 ? "blocked" : "error");
        return;
      }
      routeAfterLogin();
    } catch {
      setStatus("error");
    }
  };

  if (status === "confirm" || status === "verifying") {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>ログインを完了します</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, lineHeight: 1.6 }}>
          下のボタンを押すとログインします。
        </p>
        <button
          type="button"
          onClick={handleVerify}
          disabled={status === "verifying"}
          style={{ padding: "14px 40px", background: "#534AB7", color: "white", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: status === "verifying" ? "default" : "pointer", opacity: status === "verifying" ? 0.6 : 1 }}
        >
          {status === "verifying" ? "ログイン中..." : "ログインする"}
        </button>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>認証中...</h1>
        <p style={{ color: "#6b6b68" }}>しばらくお待ちください</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E1F5EE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 26, color: "#1D9E75" }}>{"\u2713"}</div>
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>ログイン成功</h1>
        <p style={{ color: "#6b6b68", marginBottom: 16 }}>ダッシュボードに移動します...</p>
        <p style={{ fontSize: 13, color: "#999" }}>自動的にリダイレクトされない場合は <a href="/dashboard" style={{ color: "#534AB7" }}>こちら</a></p>
      </div>
    );
  }

  if (status === "connecting") {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#EEEDFE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 26, color: "#534AB7" }}>CS</div>
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>HubSpot と連携します</h1>
        <p style={{ color: "#6b6b68", marginBottom: 16 }}>初回ログインのため、HubSpot の認可画面に移動します...</p>
        <p style={{ fontSize: 13, color: "#999" }}>自動的にリダイレクトされない場合は <a href="/api/auth/authorize" style={{ color: "#534AB7" }}>こちら</a></p>
      </div>
    );
  }

  if (status === "blocked") {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#A32D2D" }}>ログインを完了できませんでした</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, lineHeight: 1.6 }}>
          ブラウザの拡張機能が原因の可能性があります。<br />
          拡張機能を無効にして再試行してください。
        </p>
        <a href="/login" style={{ display: "inline-block", padding: "12px 24px", background: "#534AB7", color: "white", borderRadius: 8, textDecoration: "none", fontWeight: 600 }}>
          ログインページへ
        </a>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
      <Logo />
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#A32D2D" }}>認証に失敗しました</h1>
      <p style={{ color: "#6b6b68", marginBottom: 24, lineHeight: 1.6 }}>
        リンクが無効または期限切れです。<br />再度ログインしてください。
      </p>
      <a href="/login" style={{ display: "inline-block", padding: "12px 24px", background: "#534AB7", color: "white", borderRadius: 8, textDecoration: "none", fontWeight: 600 }}>
        ログインページへ
      </a>
    </div>
  );
}

// ─── メール変更確認ページ ───
export function ConfirmEmailPage() {
  const params = new URLSearchParams(window.location.search);
  /** サーバーが付けた結果（旧経路との互換のため残す） */
  const statusParam = params.get("status");
  const token = params.get("token");
  /** ボタン押下の結果。statusParam より優先する */
  const [result, setResult] = useState<"success" | "invalid" | "expired" | "blocked" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const status = result ?? statusParam;

  useEffect(() => {
    /**
     * メール内リンク（?token=xxx）で来た場合は **ここで確定しない**。
     *
     * 以前はこの時点で /api/auth/confirm-email へ自動遷移していたため、
     * メールスキャナが URL を開くだけでメールアドレス変更が確定していた
     * （ops/MAGIC_LINK_SAFELINKS.md）。ボタンを押すまで何もしない。
     */
    if (status === "success") {
      const timer = setTimeout(() => {
        window.location.href = "/dashboard";
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  /** ボタン押下で初めて確定する */
  const handleConfirm = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/confirm-email", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (res.ok) {
        setResult("success");
        return;
      }
      // 403 は Origin が付かなかった場合。リンク自体は有効なので分けて出す
      if (res.status === 403) {
        setResult("blocked");
        return;
      }
      const body = await res.json().catch(() => ({}));
      setResult(body?.code === "expired" ? "expired" : "invalid");
    } catch {
      setResult("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  if (!status && token) {
    return (
      <div style={{ maxWidth: 480, margin: "160px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>メールアドレスの変更を確定します</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
          下のボタンを押すと変更が確定します。
        </p>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          style={{ padding: "14px 40px", background: "#534AB7", color: "white", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}
        >
          {submitting ? "確定中..." : "変更を確定する"}
        </button>
      </div>
    );
  }

  if (status === "blocked") {
    return (
      <div style={{ maxWidth: 480, margin: "160px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: "#A32D2D" }}>確定できませんでした</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
          ブラウザの拡張機能が原因の可能性があります。<br />
          拡張機能を無効にして再試行してください。
        </p>
        <a href="/dashboard" style={{ display: "inline-block", padding: "12px 24px", background: "#534AB7", color: "white", borderRadius: 8, textDecoration: "none", fontWeight: 600 }}>
          ダッシュボードへ
        </a>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div style={{ maxWidth: 480, margin: "160px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <Logo />
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>メールアドレスを変更しました</h1>
        <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
          新しいメールアドレスが有効になりました。<br />
          3秒後にダッシュボードに戻ります...
        </p>
        <a href="/dashboard" style={{ display: "inline-block", padding: "12px 24px", background: "#534AB7", color: "white", borderRadius: 8, textDecoration: "none", fontWeight: 600 }}>
          今すぐダッシュボードへ
        </a>
      </div>
    );
  }

  const msg =
    status === "expired"
      ? "リンクの有効期限が切れています。もう一度メールアドレスの変更を申請してください。"
      : "リンクが無効です。もう一度メールアドレスの変更を申請してください。";

  return (
    <div style={{ maxWidth: 480, margin: "160px auto", textAlign: "center", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
      <Logo />
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: "#A32D2D" }}>変更できませんでした</h1>
      <p style={{ color: "#6b6b68", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>{msg}</p>
      <a href="/dashboard" style={{ display: "inline-block", padding: "12px 24px", background: "#534AB7", color: "white", borderRadius: 8, textDecoration: "none", fontWeight: 600 }}>
        ダッシュボードへ
      </a>
    </div>
  );
}

// ─── メインアプリ ───
export default function App() {
  // URL からタブを決める。リロード・共有・戻るボタンで同じ画面に戻れるようにする
  const [activeTab, setActiveTab] = useState<Tab>(
    () => parseRoute(window.location.pathname, window.location.search).tab,
  );
  /** Stripe からの戻り（status=success）。請求タブで完了を知らせる */
  const [billingStatus, setBillingStatus] = useState<string | null>(
    () => parseRoute(window.location.pathname, window.location.search).status,
  );
  // 認証判定は httpOnly Cookie のみを根拠にし、/api/tenants/me の成否で決める。
  // JWT の直接保持は XSS 盗難リスクがあるため廃止。
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [tenantInfo, setTenantInfo] = useState<{ companyName: string; portalId: string } | null>(null);
  const [userInfo, setUserInfo] = useState<{ email: string; name: string | null; role: string } | null>(null);
  const [authReady, setAuthReady] = useState(false); // 認証検証完了フラグ

  /**
   * タブを切り替えて URL も更新する。
   * pushState なのでブラウザの戻る/進むが効く。
   */
  const goToTab = useCallback((tab: Tab) => {
    setActiveTab(tab);
    const next = pathForTab(tab);
    if (window.location.pathname !== next) {
      window.history.pushState({ tab }, "", next);
      setCurrentPath(next);
    }
  }, []);

  // 戻る/進むでタブを追従させる
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setCurrentPath(path);
      if (isDashboardPath(path)) {
        setActiveTab(parseRoute(path, window.location.search).tab);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /**
   * 旧形式（?tab=）で来たら新しいパスへ置き換える。
   * Stripe の戻り先が /dashboard?tab=billing&status=success のままなので、
   * 受け取れる状態は保ちつつ URL は正規化する。
   * replaceState なので「戻る」で旧 URL に戻らない。
   */
  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    const parsed = parseRoute(window.location.pathname, window.location.search);
    if (!parsed.legacy) return;
    const next = pathForTab(parsed.tab);
    window.history.replaceState({ tab: parsed.tab }, "", next);
    setCurrentPath(next);
  }, [authReady, isLoggedIn]);

  // ロールで開けないタブは寄せる（admin は請求タブを持たない）
  useEffect(() => {
    if (!userInfo?.role) return;
    const allowed = resolveTabForRole(activeTab, userInfo.role);
    if (allowed !== activeTab) goToTab(allowed);
  }, [userInfo?.role, activeTab, goToTab]);

  // テナント情報を取得（検証完了まではダッシュボードを表示しない）
  useEffect(() => {
    api.getMe().then((t: any) => {
      setTenantInfo({ companyName: t.companyName, portalId: t.portalId });
      if (t.user) setUserInfo({ email: t.user.email, name: t.user.name, role: t.user.role });
      setIsLoggedIn(true);
      setAuthReady(true);
    }).catch(() => {
      // 401 等 → 未ログインとして扱う
      setIsLoggedIn(false);
      setAuthReady(true);
    });
  }, []);

  // GTM 連携: ダッシュボード表示確定時に dataLayer に通知（公開ページ・LPでは発火しない）
  // タブ切り替え時にも再送し、GTM 側でタブごとの計測も可能にする
  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    // 公開ページ（/faq, /terms, /privacy, /contact, /register, /login, /auth/*）では発火させない
    const PUBLIC_PATHS = ["/faq", "/terms", "/privacy", "/contact", "/guide", "/register", "/login"];
    if (PUBLIC_PATHS.includes(currentPath)) return;
    if (currentPath.startsWith("/auth/")) return;
    if (currentPath.startsWith("/guide/")) return;

    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "dashboard_view",
      app_section: "dashboard",
      active_tab: activeTab,
      user_role: userInfo?.role,
    });
  }, [authReady, isLoggedIn, activeTab, userInfo?.role, currentPath]);

  // パスに基づいてページを表示（公開ページ）
  if (currentPath === "/faq") return <FaqPage />;
  if (currentPath === "/terms") return <TermsPage />;
  if (currentPath === "/privacy") return <PrivacyPage />;
  if (currentPath === "/contact") return <ContactPage />;
  if (currentPath === "/guide") return <GuidePage slug={null} />;
  if (currentPath.startsWith("/guide/")) {
    const slug = currentPath.slice("/guide/".length).split("/")[0] || null;
    return <GuidePage slug={slug} />;
  }
  if (currentPath === "/register") return <RegisterPage />;
  if (currentPath === "/login") return <LoginPage />;
  if (currentPath.startsWith("/auth/verify")) return <VerifyPage />;
  if (currentPath.startsWith("/auth/confirm-email")) return <ConfirmEmailPage />;

  // ルート "/" は、未ログインならランディングページ、ログイン済みならダッシュボードへ
  // （サーバーの /api/tenants/me 結果がまだなら authReady 待ちで下のローディング表示に回す）
  if ((currentPath === "/" || currentPath === "") && authReady && !isLoggedIn) {
    return <LandingPage />;
  }

  // 認証検証が完了するまでローディング表示
  if (!authReady) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 10, background: "#534AB7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <span style={{ color: "white", fontSize: 20, fontWeight: 600 }}>CS</span>
          </div>
          <p style={{ color: "#6b6b68", fontSize: 15 }}>読み込み中...</p>
        </div>
      </div>
    );
  }

  // 未ログイン → ログインページにリダイレクト
  if (!isLoggedIn) {
    return <LoginPage />;
  }

  const inner: React.CSSProperties = { maxWidth: 1280, margin: "0 auto", padding: "0 40px", width: "100%" };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header style={{ width: "100%", background: "white", borderBottom: "1px solid #e8e7e3" }}>
        <div style={{ ...inner, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img
              src={LOGO_URL}
              alt="Sign Extension for クラウドサイン"
              style={{ height: 36, width: "auto", display: "block" }}
            />
            <div>
              <div style={{ fontSize: 13, color: "#6b6b68" }}>{tenantInfo ? `${tenantInfo.companyName} (${tenantInfo.portalId})` : "読み込み中..."}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <NotificationBell />
            <AccountSwitcher
              userInfo={userInfo}
              tenantInfo={tenantInfo}
              onLogout={async () => {
                await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
                setIsLoggedIn(false);
                setCurrentPath("/login");
                window.history.pushState({}, "", "/login");
              }}
            />
          </div>
        </div>
      </header>

      <nav style={{ width: "100%", background: "white", borderBottom: "1px solid #e8e7e3" }}>
        <div style={{ ...inner, display: "flex", justifyContent: "flex-start", gap: 4, padding: "0 40px" }}>
          {TABS.filter((tab) => {
            const role = userInfo?.role;
            if (!role) return true; // 移行期: role 不明なら全表示
            if (role === "owner") return true;
            if (role === "admin") return tab.key !== "billing";
            if (role === "member") return tab.key === "dashboard" || tab.key === "settings";
            return true;
          }).map((tab) => (
            <button key={tab.key} onClick={() => goToTab(tab.key)} style={{
              background: "none", border: "none",
              borderBottom: activeTab === tab.key ? "2.5px solid #534AB7" : "2.5px solid transparent",
              padding: "14px 20px", fontSize: 15,
              color: activeTab === tab.key ? "#1a1a1a" : "#6b6b68",
              fontWeight: activeTab === tab.key ? 600 : 400,
              cursor: "pointer", whiteSpace: "nowrap",
            }}>{tab.label}</button>
          ))}
        </div>
      </nav>

      <main style={{ flex: 1, width: "100%", padding: "28px 0 40px" }}>
        <div style={inner}>
          {activeTab === "dashboard" && <DashboardTab onTabChange={(tab) => goToTab(tab as Tab)} />}
          {activeTab === "logs" && <LogsTab />}
          {activeTab === "connection" && <ConnectionTab />}
            {activeTab === "users" && <UsersTab />}
          {activeTab === "hubspot-users" && <HubSpotUsersTab />}
          {activeTab === "settings" && <SettingsTab />}
          {activeTab === "billing" && <BillingTab status={billingStatus} />}
        </div>
      </main>

      <footer style={{ width: "100%", background: "white", borderTop: "1px solid #e8e7e3" }}>
        <div style={{ ...inner, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "20px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#6b6b68" }}>
            <img
              src={LOGO_URL}
              alt="Sign Extension for クラウドサイン"
              style={{ height: 22, width: "auto", display: "block" }}
            />
            <span>v1.0.0 / 運営: <a href="https://now-village.jp" target="_blank" rel="noopener noreferrer" style={{ color: "#534AB7", textDecoration: "none" }}>ナウビレッジ株式会社</a></span>
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {[
              { label: "利用規約", href: "/terms", external: false },
              { label: "個人情報の取扱い", href: "/privacy", external: false },
              { label: "利用ガイド", href: "/guide", external: false },
              { label: "FAQ・ヘルプ", href: "/faq", external: false },
              { label: "お問い合わせ", href: "/contact", external: false },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : "_self"}
                rel={item.external ? "noopener noreferrer" : undefined}
                style={{ fontSize: 13, color: "#6b6b68", textDecoration: "none" }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
